import {
  BarChart3,
  Building2,
  ClipboardList,
  Clock,
  Gift,
  Handshake,
  Landmark,
  LayoutDashboard,
  List,
  Mail,
  Map,
  MessageSquare,
  ScrollText,
  Settings,
  Tag,
  type LucideIcon,
  User,
  UserCog,
  Users,
  Wallet,
  Wrench
} from 'lucide-react'

export interface NavItem {
  /** Route path, or an external URL when `external` is true. For a group
   * (an item with `children`) this is just a stable id, not a route. */
  key: string
  /** i18n key under the `layout` namespace `navigation.*`. */
  labelKey: string
  icon: LucideIcon
  external?: boolean
  /** Optional predicate to conditionally show the item. */
  show?: (user?: NavUser) => boolean
  /** When set, the item renders as a collapsible dropdown of sub-items. */
  children?: NavItem[]
  /** Renders the item non-clickable and dimmed. */
  disabled?: boolean
  /** i18n key (layout namespace) for the tooltip shown on a disabled item. */
  disabledTooltipKey?: string
}

export interface NavUser {
  role?: string | null
  email?: string | null
  first_name?: string | null
  last_name?: string | null
  has_contract?: boolean | string | null
  is_partner?: boolean | null
}

const livabilityNav: NavItem[] = [
  { key: 'apartments-search', labelKey: 'aiSearch', icon: MessageSquare },
  { key: 'apartments', labelKey: 'apartmentsList', icon: List },
  { key: 'apartments-map', labelKey: 'apartmentsMap', icon: Map }
]

function withRolePrefix(role: 'user' | 'admin', items: NavItem[]): NavItem[] {
  return items.map((item) => ({
    ...item,
    key: `/${role}/${item.key}`
  }))
}

/** The three livability views grouped into a collapsible dropdown. */
function livabilityGroup(role: 'user' | 'admin'): NavItem {
  return {
    key: `livability-${role}`,
    labelKey: 'housing',
    icon: Building2,
    children: withRolePrefix(role, livabilityNav)
  }
}

const userNav: NavItem[] = [
  { key: '/user/dashboard', labelKey: 'dashboard', icon: LayoutDashboard },
  { key: '/user/profile', labelKey: 'profile', icon: User },
  { key: '/user/application', labelKey: 'application', icon: ClipboardList },
  { key: '/user/offer', labelKey: 'offer', icon: Tag },
  { key: '/user/tickets', labelKey: 'tickets', icon: Wrench },
  livabilityGroup('user'),
  {
    key: '/user/contract',
    labelKey: 'rent',
    icon: ScrollText,
    show: (u) => !!u?.has_contract
  },
  {
    key: '/user/referrals',
    labelKey: 'referrals',
    icon: Gift,
    show: (u) => !!u?.is_partner
  }
]

const adminNav: NavItem[] = [
  { key: '/admin/dashboard', labelKey: 'dashboard', icon: LayoutDashboard },
  { key: '/admin/event-analytics', labelKey: 'eventAnalytics', icon: BarChart3 },
  { key: '/admin/applications', labelKey: 'applications', icon: ClipboardList },
  { key: '/admin/users', labelKey: 'users', icon: Users },
  { key: '/admin/flats', labelKey: 'flats', icon: Building2 },
  livabilityGroup('admin'),
  { key: '/admin/offers', labelKey: 'offers', icon: Tag },
  { key: '/admin/tickets', labelKey: 'tickets', icon: Wrench },
  { key: '/admin/landlords', labelKey: 'landlords', icon: UserCog },
  { key: '/admin/partners', labelKey: 'partners', icon: Handshake },
  { key: '/admin/send-email', labelKey: 'send-email', icon: Mail },
  { key: '/admin/bank-notifications', labelKey: 'bank-notifications', icon: Landmark },
  { key: '/admin/cron-jobs', labelKey: 'cron-jobs', icon: Clock }
]

const landlordNav: NavItem[] = [
  { key: '/landlord/dashboard', labelKey: 'dashboard', icon: LayoutDashboard },
  { key: '/landlord/profile', labelKey: 'profile', icon: User },
  { key: '/landlord/flats', labelKey: 'flats', icon: Building2 },
  { key: '/landlord/payments', labelKey: 'payments', icon: Wallet }
]

export const settingsNavItem: NavItem = {
  key: '/user/settings',
  labelKey: 'settings',
  icon: Settings
}

export function isUserRole(role?: string | null): boolean {
  return role === 'USER' || role === 'ROOMMATE'
}

export function isLandlordRole(role?: string | null): boolean {
  return role === 'LANDLORD'
}

export function getDashboardPath(role?: string | null): string {
  if (isUserRole(role)) return '/user/dashboard'
  if (isLandlordRole(role)) return '/landlord/dashboard'
  if (role === 'ADMIN') return '/admin/dashboard'
  return '/login'
}

/** Main nav items for the given role, with conditional items resolved. */
export function getNavItems(role?: string | null, user?: NavUser): NavItem[] {
  const items = isUserRole(role)
    ? userNav
    : isLandlordRole(role)
      ? landlordNav
      : adminNav
  return items.filter((item) => (item.show ? item.show(user) : true))
}
